import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Space,
  message,
  InputNumber,
  Table,
  Tag
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  CheckOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import api from '../utils/api'
import { useNavigate } from 'react-router-dom'
import { getInventoryProducts } from '../api/inventory'
import { now as tzNow } from '../utils/timezone'

const { Option } = Select
const { TextArea } = Input

// Premium color palette
const colors = {
  primary: '#2C3E50',
  secondary: '#5D7A9B',
  accent: '#8B9DC3',
  text: {
    primary: '#1A1A1A',
    secondary: '#6B7280',
    tertiary: '#9CA3AF'
  },
  background: {
    primary: '#FFFFFF',
    secondary: '#FAFBFC',
    tertiary: '#F5F7FA'
  },
  border: '#E5E7EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444'
}

const spacing = {
  xs: '8px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '48px'
}

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${spacing.lg} ${spacing.md};
  background: ${colors.background.secondary};
  min-height: 100vh;

  @media (max-width: 820px) {
    padding: ${spacing.md} ${spacing.sm};
  }
`

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 300;
  color: ${colors.text.primary};
  margin: 0 0 ${spacing.xl} 0;
  letter-spacing: -0.5px;

  @media (max-width: 820px) {
    font-size: 24px;
    margin-bottom: ${spacing.lg};
  }
`

const FormCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: ${spacing.lg};

  .ant-card-head {
    border-bottom: 1px solid ${colors.border};
    padding: ${spacing.md} ${spacing.lg};

    .ant-card-head-title {
      font-size: 18px;
      font-weight: 500;
      color: ${colors.text.primary};
    }
  }

  .ant-card-body {
    padding: ${spacing.lg};

    @media (max-width: 820px) {
      padding: ${spacing.md};
    }
  }
`

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: ${spacing.md};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  .anticon {
    color: ${colors.secondary};
  }
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${spacing.md};

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: ${spacing.sm};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  margin-top: ${spacing.xl};
  padding-top: ${spacing.lg};
  border-top: 1px solid ${colors.border};

  @media (max-width: 820px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

const SummaryCard = styled(Card)`
  background: ${colors.background.tertiary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  margin-top: ${spacing.lg};

  .ant-card-body {
    padding: ${spacing.lg};
  }
`

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.sm} 0;
  font-size: 14px;

  &.total {
    font-size: 18px;
    font-weight: 500;
    color: ${colors.text.primary};
    border-top: 1px solid ${colors.border};
    margin-top: ${spacing.sm};
    padding-top: ${spacing.md};
  }
`

const SummaryLabel = styled.span`
  color: ${colors.text.secondary};
`

const SummaryValue = styled.span`
  color: ${colors.text.primary};
  font-weight: 500;
`

const EmployeeOrderPage = () => {
  const [form] = Form.useForm()
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState([])
  const [inventoryProducts, setInventoryProducts] = useState([])
  const [inventoryCategories, setInventoryCategories] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([])
  const [orderType, setOrderType] = useState('purchase') // 'purchase' 自購, 'material' 領料, 'gift' 贈出
  const [paymentMethod, setPaymentMethod] = useState('cash') // 'cash', 'nonCash', 'salaryDeduct'
  const initializedRef = useRef(false)

  const fetchEmployees = useCallback(async () => {
    if (!user?.businessId || !hasFeature('checkout')) return
    try {
      const response = await api.get(`/api/employees/${user.businessId}`)
      setEmployees(response.data || [])
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }, [user?.businessId, hasFeature]) // 移除 isBrandPlan 依賴，在函數內部檢查

  const fetchInventoryProducts = useCallback(async () => {
    if (!user?.businessId || !hasFeature('checkout')) return
    try {
      const response = await getInventoryProducts({
        page: 1,
        pageSize: 1000,
        status: 'active'
      })
      const productsData =
        response.data?.data?.items || response.data?.items || []
      setInventoryProducts(productsData)
    } catch (error) {
      console.error('獲取庫存商品列表失敗:', error)
      setInventoryProducts([])
    }
  }, [user?.businessId, hasFeature]) // 移除 isBrandPlan 依賴，在函數內部檢查

  const fetchInventoryCategories = useCallback(async () => {
    if (!user?.businessId || !hasFeature('checkout')) return
    try {
      const response = await api.get('/api/store/categories')
      let categoriesData = []
      if (Array.isArray(response.data?.data)) {
        categoriesData = response.data.data
      } else if (Array.isArray(response.data)) {
        categoriesData = response.data
      }
      setInventoryCategories(categoriesData)
    } catch (error) {
      console.error('獲取庫存商品分類失敗:', error)
      setInventoryCategories([])
    }
  }, [user?.businessId, hasFeature]) // 移除 isBrandPlan 依賴，在函數內部檢查

  useEffect(() => {
    if (!hasFeature('checkout') || initializedRef.current) return
    // 只在組件掛載時執行一次
    initializedRef.current = true
    fetchEmployees()
    fetchInventoryProducts()
    fetchInventoryCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 只在掛載時執行一次

  const handleAddProduct = () => {
    const newProduct = {
      productId: null,
      quantity: 1,
      price: 0,
      key: Date.now()
    }
    setSelectedProducts([...selectedProducts, newProduct])
  }

  const handleRemoveProduct = (key) => {
    setSelectedProducts(selectedProducts.filter((item) => item.key !== key))
  }

  const handleProductChange = (key, field, value) => {
    setSelectedProducts(
      selectedProducts.map((item) => {
        if (item.key === key) {
          const updated = { ...item, [field]: value }
          // 如果選擇了商品，自動填充價格
          if (field === 'productId' && value) {
            const product = inventoryProducts.find((p) => p._id === value)
            if (product) {
              updated.price = product.suggestedPrice || product.price || 0
            }
          }
          // 計算總價
          if (field === 'quantity' || field === 'price') {
            updated.totalPrice = (updated.quantity || 1) * (updated.price || 0)
          } else if (field === 'productId') {
            updated.totalPrice = (updated.quantity || 1) * (updated.price || 0)
          }
          return updated
        }
        return item
      })
    )
  }

  const totalProductAmount = selectedProducts.reduce(
    (sum, item) => sum + (Number(item.totalPrice) || 0),
    0
  )

  const totalAmount = totalProductAmount

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      // 所有類型都只需要商品
      if (selectedProducts.length === 0) {
        message.warning('請至少選擇一項商品')
        return
      }

      setLoading(true)

      const requestData = {
        type: orderType, // 'purchase' 自購, 'material' 領料, 'gift' 贈出
        employeeId: values.employeeId,
        employeeName: employees.find((e) => e._id === values.employeeId)?.name,
        paymentMethod: paymentMethod, // 'cash', 'nonCash', 'salaryDeduct'
        products: selectedProducts
          .filter((p) => p.productId)
          .map((p) => ({
            productId: p.productId,
            quantity: Number(p.quantity) || 1,
            price: Number(p.price) || 0
          })),
        date: values.date.format('YYYY-MM-DD'),
        time: values.time.format('HH:mm'),
        remarks: values.remarks || '',
        totals: {
          productsAmount: totalProductAmount,
          grandTotal: totalAmount
        },
        payment: {
          cash: paymentMethod === 'cash' ? totalAmount : 0,
          transfer: paymentMethod === 'nonCash' ? totalAmount : 0,
          receivable: paymentMethod === 'salaryDeduct' ? totalAmount : 0
        }
      }

      const response = await api.post(
        `/api/businesses/${user.businessId}/checkout/employee-order`,
        requestData
      )

      if (response.data.success) {
        message.success('訂單建立成功')
        // 重置表單
        form.resetFields()
        setSelectedProducts([])
        setOrderType('purchase')
        setPaymentMethod('cash')
        // 重置表單的初始值
        form.setFieldsValue({
          orderType: 'purchase',
          paymentMethod: 'cash',
          date: tzNow(),
          time: tzNow()
        })
        // 可選：導航到記錄頁面
        // navigate('/checkout/employee-records')
      }
    } catch (error) {
      console.error('提交訂單失敗:', error)
      message.error(
        error.response?.data?.message || error.message || '提交訂單失敗'
      )
    } finally {
      setLoading(false)
    }
  }

  if (!hasFeature('checkout')) {
    return (
      <PageContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <p style={{ fontSize: '16px', color: colors.text.secondary }}>
              此功能需要更高級別的會員方案才能使用
            </p>
            <Button
              type='primary'
              onClick={() => navigate('/settings/membership')}
              style={{ marginTop: '16px' }}
            >
              查看會員方案
            </Button>
          </div>
        </Card>
      </PageContainer>
    )
  }

  const productColumns = [
    {
      title: '商品',
      key: 'product',
      render: (_, record) => (
        <Select
          style={{ width: '100%' }}
          placeholder='選擇商品'
          value={record.productId}
          onChange={(value) =>
            handleProductChange(record.key, 'productId', value)
          }
          showSearch
          optionFilterProp='children'
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {inventoryProducts
            .filter((p) => p.status === 'active' && (p.stock || 0) > 0)
            .map((product) => (
              <Option key={product._id} value={product._id}>
                {product.name} {product.stock ? `(庫存: ${product.stock})` : ''}
              </Option>
            ))}
        </Select>
      )
    },
    {
      title: '數量',
      key: 'quantity',
      width: 120,
      render: (_, record) => {
        const product = inventoryProducts.find(
          (p) => p._id === record.productId
        )
        const maxStock = product?.stock || 0
        return (
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            max={maxStock}
            value={record.quantity}
            onChange={(value) =>
              handleProductChange(record.key, 'quantity', value)
            }
          />
        )
      }
    },
    {
      title: '單價',
      key: 'price',
      width: 150,
      render: (_, record) => (
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          value={record.price}
          onChange={(value) => handleProductChange(record.key, 'price', value)}
          formatter={(value) =>
            `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          }
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
        />
      )
    },
    {
      title: '小計',
      key: 'totalPrice',
      width: 120,
      render: (_, record) => (
        <span style={{ fontWeight: 500 }}>
          ${(record.totalPrice || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type='text'
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.key)}
        />
      )
    }
  ]

  return (
    <PageContainer>
      <PageTitle>員工自購或領料打單</PageTitle>

      <Form form={form} layout='vertical'>
        <FormCard
          title={
            <Space>
              <UserOutlined />
              <span>員工資訊</span>
            </Space>
          }
        >
          <FormRow>
            <Form.Item
              name='employeeId'
              label='員工'
              rules={[{ required: true, message: '請選擇員工' }]}
            >
              <Select
                placeholder='選擇員工'
                showSearch
                optionFilterProp='children'
                style={{ width: '100%' }}
              >
                {employees.map((employee) => (
                  <Option key={employee._id} value={employee._id}>
                    {employee.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name='orderType'
              label='類型'
              rules={[{ required: true, message: '請選擇類型' }]}
              initialValue='purchase'
            >
              <Select
                value={orderType}
                onChange={setOrderType}
                style={{ width: '100%' }}
              >
                <Option value='purchase'>自購</Option>
                <Option value='material'>領料</Option>
                <Option value='gift'>贈出</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='paymentMethod'
              label='購買方式'
              rules={[{ required: true, message: '請選擇購買方式' }]}
              initialValue='cash'
            >
              <Select
                value={paymentMethod}
                onChange={setPaymentMethod}
                style={{ width: '100%' }}
              >
                <Option value='cash'>現金（併入當日收入）</Option>
                <Option value='nonCash'>非現金（併入當日收入）</Option>
                <Option value='salaryDeduct'>
                  由薪資扣除（會在員工當月薪資減項顯示）
                </Option>
              </Select>
            </Form.Item>
          </FormRow>
        </FormCard>

        <FormCard
          title={
            <Space>
              <ShoppingOutlined />
              <span>選擇商品</span>
            </Space>
          }
        >
          <SectionTitle>
            <ShoppingOutlined />
            <span>庫存商品</span>
          </SectionTitle>
          <Table
            columns={productColumns}
            dataSource={selectedProducts}
            rowKey='key'
            pagination={false}
            size='small'
            style={{ marginBottom: spacing.lg }}
          />
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={handleAddProduct}
            block
          >
            添加商品
          </Button>
        </FormCard>

        <FormCard
          title={
            <Space>
              <CalendarOutlined />
              <span>日期時間</span>
            </Space>
          }
        >
          <FormRow>
            <Form.Item
              name='date'
              label='日期'
              rules={[{ required: true, message: '請選擇日期' }]}
              initialValue={tzNow()}
            >
              <DatePicker style={{ width: '100%' }} format='YYYY-MM-DD' />
            </Form.Item>

            <Form.Item
              name='time'
              label='時間'
              rules={[{ required: true, message: '請選擇時間' }]}
              initialValue={tzNow()}
            >
              <DatePicker.TimePicker style={{ width: '100%' }} format='HH:mm' />
            </Form.Item>
          </FormRow>

          <Form.Item name='remarks' label='備註'>
            <TextArea rows={3} placeholder='輸入備註（可選）' />
          </Form.Item>
        </FormCard>

        <SummaryCard>
          <SummaryRow>
            <SummaryLabel>商品小計</SummaryLabel>
            <SummaryValue>${totalProductAmount.toLocaleString()}</SummaryValue>
          </SummaryRow>
          <SummaryRow className='total'>
            <SummaryLabel>總計</SummaryLabel>
            <SummaryValue>${totalAmount.toLocaleString()}</SummaryValue>
          </SummaryRow>
        </SummaryCard>

        <ButtonGroup>
          <Button onClick={() => navigate('/checkout/employee-records')}>
            查看記錄
          </Button>
          <Button
            type='primary'
            size='large'
            icon={<CheckOutlined />}
            onClick={handleSubmit}
            loading={loading}
          >
            提交訂單
          </Button>
        </ButtonGroup>
      </Form>
    </PageContainer>
  )
}

export default EmployeeOrderPage
